import type {
    ChartAISummary,
    ChartType,
    ColumnDefinition,
    DataRow,
} from "@compass/shared/schema/chart";
import { useMatches, useNavigate, useParams } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    chartDetailRoute,
    chartEditRoute,
    chartVersionEditRoute,
    chartVersionRoute,
} from "app/router";
import { Button, CardTitle, Spinner } from "components/elements";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { DiscussionComments } from "components/layout/discussion/DiscussionComments";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "components/shad-components/ui/sheet";
import { useUserTimezone } from "hooks/useUserTimezone";
import { trpc } from "lib/trpc";
import {
    ChartPreviewCard,
    DetailsFormCard,
    ExpandedPreviewOverlay,
    InputsCard,
    type PermissionSummaryEntry,
    PermissionsSummaryCard,
} from "./ChartEditorComponents";
import { PermissionsModal } from "./PermissionsModal";

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const ChartDetailScreen = () => {
    const { id } = useParams({ from: chartDetailRoute.id });
    const matches = useMatches();
    const navigate = useNavigate();
    const timeZone = useUserTimezone();

    const chartId = Number(id);

    // Derive tab and versionId from URL
    const activeTab = useMemo<"edit" | "view">(() => {
        return matches.some(
            (m) => m.route.id === chartEditRoute.id || m.route.id === chartVersionEditRoute.id,
        )
            ? "edit"
            : "view";
    }, [matches]);

    const urlVersionId = useMemo(() => {
        const versionMatch = matches.find(
            (m) => m.route.id === chartVersionRoute.id || m.route.id === chartVersionEditRoute.id,
        );
        const versionId = (versionMatch?.params as { versionId?: string })?.versionId;
        return versionId ? Number(versionId) : undefined;
    }, [matches]);

    const {
        data: chart,
        isLoading,
        refetch,
    } = trpc.charts.get.useQuery(
        { id: chartId },
        {
            refetchInterval: (query) => {
                const data = query.state.data;
                if (!data) return false;
                const hasPending = data.versions.some((v) => v.aiSummaryStatus === "PENDING");
                return hasPending ? 3000 : false;
            },
        },
    );
    const saveDraftMutation = trpc.charts.saveDraft.useMutation();
    const commitMutation = trpc.charts.commitVersion.useMutation();

    const updateMutation = trpc.charts.update.useMutation();
    const generateAIMutation = trpc.charts.generateAISummary.useMutation({
        onSuccess: () => refetch(),
        onError: () => enqueueSnackbar("Failed to request AI summary", { variant: "error" }),
    });
    const clearAIMutation = trpc.charts.clearAISummary.useMutation({
        onSuccess: () => refetch(),
        onError: () => enqueueSnackbar("Failed to clear AI summary", { variant: "error" }),
    });
    const deleteMutation = trpc.charts.delete.useMutation();
    const getVersionThread = trpc.charts.getVersionThread.useMutation();
    const chartReplyMutation = trpc.threads.createComment.useMutation();
    const chartQueryUtils = trpc.useUtils();

    const [columns, setColumns] = useState<ColumnDefinition[]>([]);
    const [rows, setRows] = useState<DataRow[]>([]);
    const [viewChartType, setViewChartType] = useState<ChartType>("LINE");
    const [commitLabel, setCommitLabel] = useState("");
    const [commitChartType, setCommitChartType] = useState<ChartType>("LINE");
    const [showCommitModal, setShowCommitModal] = useState(false);
    const [showPermissions, setShowPermissions] = useState(false);
    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editPreviewChartType, setEditPreviewChartType] = useState<ChartType>("LINE");
    const [expandedPreview, setExpandedPreview] = useState(false);
    const [threadPanelOpen, setThreadPanelOpen] = useState(false);
    const [threadPanelVersionId, setThreadPanelVersionId] = useState<number | null>(null);
    const [threadId, setThreadId] = useState<number | null>(null);
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [showAIConfirm, setShowAIConfirm] = useState(false);

    const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isOwner = chart?.currentUserPermission === "OWNER";
    const canEdit = isOwner || chart?.currentUserPermission === "EDITOR";

    const permissionEntries = useMemo<PermissionSummaryEntry[]>(
        () =>
            (chart?.permissions ?? []).map((p) => ({
                key: String(p.id),
                label:
                    p.target === "COMPASS_TEAM"
                        ? "Compass team"
                        : p.target === "ORGANIZATION"
                          ? (p.organizationName ?? "Organization")
                          : (p.userName ?? "User"),
                role: p.role,
            })),
        [chart?.permissions],
    );

    const permissionSummary = useMemo(() => {
        const parts: string[] = [];
        for (const p of chart?.permissions ?? []) {
            if (p.target === "COMPASS_TEAM") parts.push("Compass team");
            else if (p.target === "ORGANIZATION" && p.organizationName)
                parts.push(p.organizationName);
            else if (p.target === "USER" && p.userName) parts.push(p.userName);
        }
        return parts.length > 0 ? `Shared with ${parts.join(" & ")}` : "No permissions configured";
    }, [chart?.permissions]);

    // Derive selectedVersionId from URL, falling back to activeVersionId
    const selectedVersionId = urlVersionId ?? chart?.activeVersionId ?? null;

    // Initialize editor columns/rows and settings fields from chart data
    useEffect(() => {
        if (chart) {
            setEditTitle(chart.title);
            setEditDescription(chart.description ?? "");
            if (chart.draftData) {
                setColumns(chart.draftData.columns);
                setRows(chart.draftData.rows);
            } else if (chart.versions.length > 0) {
                const active =
                    chart.versions.find((v) => v.id === chart.activeVersionId) ?? chart.versions[0];
                setColumns(active.columns);
                setRows(active.rows);
                setViewChartType(
                    active.defaultChartType === "PIE" ? "LINE" : active.defaultChartType,
                );
            }
        }
    }, [chart]);

    // Update viewChartType when selected version changes
    useEffect(() => {
        if (chart && selectedVersionId) {
            const version = chart.versions.find((v) => v.id === selectedVersionId);
            if (version) {
                setViewChartType(
                    version.defaultChartType === "PIE" ? "LINE" : version.defaultChartType,
                );
            }
        }
    }, [chart, selectedVersionId]);

    // Auto-save draft with debounce
    const saveDraft = useCallback(
        (cols: ColumnDefinition[], rws: DataRow[]) => {
            if (!canEdit || !chartId) return;
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
            draftTimerRef.current = setTimeout(() => {
                saveDraftMutation.mutate({
                    id: chartId,
                    data: { columns: cols, rows: rws },
                });
            }, 2000);
        },
        [canEdit, chartId, saveDraftMutation],
    );

    const handleDataChange = useCallback(
        (newCols: ColumnDefinition[], newRows: DataRow[]) => {
            setColumns(newCols);
            setRows(newRows);
            saveDraft(newCols, newRows);
        },
        [saveDraft],
    );

    const handleCommit = async () => {
        if (!commitLabel.trim()) return;
        try {
            await commitMutation.mutateAsync({
                id: chartId,
                label: commitLabel.trim(),
                defaultChartType: commitChartType,
            });
            setCommitLabel("");
            setShowCommitModal(false);
            enqueueSnackbar("Version committed", { variant: "success" });
            refetch();
        } catch {
            enqueueSnackbar("Failed to commit version", { variant: "error" });
        }
    };

    const handleViewVersion = (versionId: number) => {
        navigate({
            to: "/visualizations/$id/$versionId",
            params: { id, versionId: String(versionId) },
        });
    };

    const handleOpenThread = async (versionId: number) => {
        setThreadPanelVersionId(versionId);
        setThreadPanelOpen(true);
        setThreadId(null);
        setShowReplyForm(false);
        try {
            const result = await getVersionThread.mutateAsync({ versionId });
            setThreadId(result.threadId);
        } catch {
            enqueueSnackbar("Failed to open discussion", { variant: "error" });
        }
    };

    const commentsQuery = trpc.threads.getComments.useQuery(
        { threadId: threadId! },
        { enabled: !!threadId },
    );

    const threadPanelVersion = threadPanelVersionId
        ? chart?.versions.find((v) => v.id === threadPanelVersionId)
        : null;

    if (isLoading) return <Spinner />;
    if (!chart) return <div className="p-6">Chart not found</div>;

    const selectedVersion = chart.versions.find((v) => v.id === selectedVersionId);

    return (
        <div className="py-6">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <CardTitle>{chart.title}</CardTitle>
                    {chart.description && (
                        <p className="mt-1 text-sm text-gray-500">{chart.description}</p>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-4 flex gap-1 border-b">
                <button
                    onClick={() => navigate({ to: "/visualizations/$id", params: { id } })}
                    className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                        activeTab === "view"
                            ? "border-indigo-500 text-indigo-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                    View
                </button>
                <button
                    onClick={() => navigate({ to: "/visualizations/$id/edit", params: { id } })}
                    className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                        activeTab === "edit"
                            ? "border-indigo-500 text-indigo-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                    Edit
                </button>
            </div>

            {/* Edit Tab */}
            {activeTab === "edit" && (
                <div className="flex gap-6">
                    {/* Left column — Inputs (data editor) */}
                    <div className="min-w-0 flex-[2]">
                        <InputsCard
                            columns={columns}
                            rows={rows}
                            onChange={handleDataChange}
                            readOnly={!canEdit}
                            exportFilename={chart.title}
                            statusText={
                                saveDraftMutation.isPending ? (
                                    <span className="text-xs text-gray-400">Saving...</span>
                                ) : undefined
                            }
                            actions={
                                <Button
                                    onClick={() => setShowCommitModal(true)}
                                    disabled={columns.length === 0}
                                >
                                    Commit Version
                                </Button>
                            }
                        />
                    </div>

                    {/* Right column — Preview, Permissions, Settings */}
                    <div className="w-96 shrink-0 space-y-4">
                        {columns.length > 1 && rows.length > 0 && (
                            <ChartPreviewCard
                                columns={columns}
                                rows={rows}
                                chartType={editPreviewChartType}
                                onChartTypeChange={setEditPreviewChartType}
                                expandedPreview={expandedPreview}
                                onToggleExpand={() => setExpandedPreview(!expandedPreview)}
                            />
                        )}

                        {/* Permissions */}
                        <PermissionsSummaryCard
                            entries={permissionEntries}
                            summaryText={permissionSummary}
                            onManage={isOwner ? () => setShowPermissions(true) : undefined}
                        />

                        {/* Settings */}
                        {isOwner && (
                            <DetailsFormCard
                                title={editTitle}
                                description={editDescription}
                                onTitleChange={setEditTitle}
                                onDescriptionChange={setEditDescription}
                                heading="Settings"
                                actions={
                                    <>
                                        <Button
                                            disabled={
                                                !editTitle.trim() ||
                                                (editTitle === chart.title &&
                                                    editDescription === (chart.description ?? ""))
                                            }
                                            onClick={async () => {
                                                await updateMutation.mutateAsync({
                                                    id: chartId,
                                                    title: editTitle.trim(),
                                                    description:
                                                        editDescription.trim() || undefined,
                                                });
                                                enqueueSnackbar("Settings saved", {
                                                    variant: "success",
                                                });
                                                refetch();
                                            }}
                                        >
                                            {updateMutation.isPending ? "Saving..." : "Save"}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={async () => {
                                                if (
                                                    confirm(
                                                        "Delete this chart? This cannot be undone.",
                                                    )
                                                ) {
                                                    await deleteMutation.mutateAsync({
                                                        id: chartId,
                                                    });
                                                    enqueueSnackbar("Chart deleted", {
                                                        variant: "success",
                                                    });
                                                    navigate({ to: "/visualizations" });
                                                }
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </>
                                }
                            />
                        )}
                    </div>
                </div>
            )}

            {/* View Tab */}
            {activeTab === "view" && (
                <div className="flex gap-6">
                    {/* Left column — Inputs */}
                    <div className="min-w-0 flex-1 space-y-4">
                        {/* Data Table */}
                        {selectedVersion ? (
                            <InputsCard
                                columns={selectedVersion.columns}
                                rows={selectedVersion.rows}
                                onChange={noop}
                                readOnly
                                exportFilename={chart.title}
                            />
                        ) : (
                            <p className="py-8 text-center text-gray-500">
                                No versions yet. Commit data from the Edit tab first.
                            </p>
                        )}

                        {/* AI Summary (moved from right column — full-width makes long summaries readable) */}
                        {selectedVersion && (
                            <>
                                <AISummaryPanel
                                    status={selectedVersion.aiSummaryStatus}
                                    summary={selectedVersion.aiSummary}
                                    generatedBy={selectedVersion.aiSummaryGeneratedBy}
                                    generatedAt={selectedVersion.aiSummaryGeneratedAt}
                                    onGenerate={() => setShowAIConfirm(true)}
                                    isGenerating={generateAIMutation.isPending}
                                    onClear={() =>
                                        clearAIMutation.mutate({
                                            chartId,
                                            versionId: selectedVersion.id,
                                        })
                                    }
                                    isClearing={clearAIMutation.isPending}
                                />
                                {showAIConfirm && (
                                    <AIConfirmDialog
                                        onConfirm={() => {
                                            setShowAIConfirm(false);
                                            generateAIMutation.mutate({
                                                chartId,
                                                versionId: selectedVersion.id,
                                            });
                                        }}
                                        onCancel={() => setShowAIConfirm(false)}
                                    />
                                )}
                            </>
                        )}

                        {/* Version selector & comments */}
                        <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                            <div>
                                <label className="mr-2 text-sm font-medium">Version:</label>
                                <select
                                    value={selectedVersionId ?? ""}
                                    onChange={(e) => handleViewVersion(Number(e.target.value))}
                                    className="rounded border px-2 py-1 text-sm"
                                >
                                    {chart.versions.map((v) => (
                                        <option key={v.id} value={v.id}>
                                            v{v.versionNumber} - {v.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {selectedVersion && (
                                <div className="ml-auto">
                                    <Button
                                        variant="outline"
                                        onClick={() => handleOpenThread(selectedVersion.id)}
                                    >
                                        Comments
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right column — Chart Preview, Details, Permissions, Version History */}
                    <div className="w-96 shrink-0 space-y-4">
                        {/* Chart Preview */}
                        {selectedVersion && (
                            <ChartPreviewCard
                                columns={selectedVersion.columns}
                                rows={selectedVersion.rows}
                                chartType={viewChartType}
                                onChartTypeChange={setViewChartType}
                                expandedPreview={expandedPreview}
                                onToggleExpand={() => setExpandedPreview(!expandedPreview)}
                            />
                        )}
                        {/* Details */}
                        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                            <h3 className="mb-3 text-sm font-semibold">Details</h3>
                            <dl className="space-y-2 text-sm">
                                <div>
                                    <dt className="text-gray-500">Author</dt>
                                    <dd className="font-medium">{chart.author.name}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-500">Created</dt>
                                    <dd>
                                        {new Date(chart.createdAt).toLocaleDateString(undefined, {
                                            timeZone,
                                        })}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-gray-500">Last Updated</dt>
                                    <dd>
                                        {new Date(chart.updatedAt).toLocaleDateString(undefined, {
                                            timeZone,
                                        })}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-gray-500">Versions</dt>
                                    <dd>{chart.versionCount}</dd>
                                </div>
                            </dl>
                        </div>

                        {/* Version History */}
                        {chart.versions.length > 0 && (
                            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                                <h3 className="mb-3 text-sm font-semibold">Version History</h3>
                                <div className="space-y-2">
                                    {chart.versions.map((v) => (
                                        <div
                                            key={v.id}
                                            className={`rounded-lg border p-2 text-sm ${
                                                v.id === chart.activeVersionId
                                                    ? "border-indigo-200 bg-indigo-50"
                                                    : ""
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium">
                                                    v{v.versionNumber}
                                                    {v.id === chart.activeVersionId && (
                                                        <span className="ml-1 text-xs text-indigo-600">
                                                            (active)
                                                        </span>
                                                    )}
                                                </span>
                                                <div className="flex gap-2">
                                                    <button
                                                        className="text-xs text-indigo-600 hover:underline"
                                                        onClick={() => handleViewVersion(v.id)}
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        className="text-xs text-gray-500 hover:underline"
                                                        onClick={() => handleOpenThread(v.id)}
                                                    >
                                                        Discuss
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="mt-1 text-xs text-gray-500">
                                                {v.label} · {v.author.name} ·{" "}
                                                {new Date(v.createdAt).toLocaleDateString(
                                                    undefined,
                                                    { timeZone },
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Expanded Preview Overlay */}
            {expandedPreview && (
                <ExpandedPreviewOverlay
                    columns={activeTab === "edit" ? columns : (selectedVersion?.columns ?? [])}
                    rows={activeTab === "edit" ? rows : (selectedVersion?.rows ?? [])}
                    chartType={activeTab === "edit" ? editPreviewChartType : viewChartType}
                    onChartTypeChange={
                        activeTab === "edit" ? setEditPreviewChartType : setViewChartType
                    }
                    onClose={() => setExpandedPreview(false)}
                />
            )}

            {/* Commit Modal */}
            {showCommitModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                    onClick={() => setShowCommitModal(false)}
                >
                    <div
                        className="w-96 rounded-xl bg-white p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="mb-4 text-lg font-semibold">Commit Version</h2>
                        <div className="mb-3">
                            <label className="mb-1 block text-sm font-medium">Version Label</label>
                            <input
                                type="text"
                                value={commitLabel}
                                onChange={(e) => setCommitLabel(e.target.value)}
                                placeholder="e.g. Q4 Data"
                                className="w-full rounded-md border px-3 py-2 text-sm"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="mb-1 block text-sm font-medium">
                                Default Chart Type
                            </label>
                            <select
                                value={commitChartType}
                                onChange={(e) => setCommitChartType(e.target.value as ChartType)}
                                className="w-full rounded-md border px-3 py-2 text-sm"
                            >
                                <option value="LINE">Line</option>
                                <option value="BAR">Bar</option>
                                {/* <option value="PIE">Pie</option> */}
                            </select>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setShowCommitModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCommit}
                                disabled={!commitLabel.trim() || commitMutation.isPending}
                            >
                                {commitMutation.isPending ? "Committing..." : "Commit"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Permissions Modal */}
            <PermissionsModal
                isOpen={showPermissions}
                onClose={() => setShowPermissions(false)}
                chartId={chartId}
                permissions={chart.permissions}
                onUpdated={() => refetch()}
            />

            {/* Comments Panel */}
            <Sheet open={threadPanelOpen} onOpenChange={setThreadPanelOpen}>
                <SheetContent side="right" className="flex flex-col sm:max-w-lg">
                    <SheetHeader>
                        <SheetTitle>
                            Comments
                            {threadPanelVersion ? ` — v${threadPanelVersion.versionNumber}` : ""}
                        </SheetTitle>
                    </SheetHeader>

                    {!threadId || commentsQuery.isLoading ? (
                        <div className="flex flex-1 items-center justify-center">
                            <Spinner />
                        </div>
                    ) : (
                        <div className="flex flex-1 flex-col overflow-hidden">
                            <div className="flex-1 overflow-y-auto">
                                {commentsQuery.data && commentsQuery.data.length > 0 ? (
                                    <DiscussionComments
                                        comments={commentsQuery.data}
                                        threadId={threadId}
                                    />
                                ) : (
                                    <p className="py-8 text-center text-sm text-gray-500">
                                        No comments yet. Start the conversation!
                                    </p>
                                )}
                            </div>

                            <div className="border-t pt-4">
                                {showReplyForm ? (
                                    <ThreadCommentForm
                                        context="discussion"
                                        onSubmit={({ contentJson, attachmentIds }) => {
                                            chartReplyMutation.mutate(
                                                {
                                                    threadId,
                                                    contentJson,
                                                    attachmentIds,
                                                },
                                                {
                                                    onSuccess: () => {
                                                        chartQueryUtils.threads.getComments.invalidate();
                                                        setShowReplyForm(false);
                                                    },
                                                },
                                            );
                                        }}
                                        onCancel={() => setShowReplyForm(false)}
                                        showCancel
                                        isLoading={chartReplyMutation.isPending}
                                        submitLabel="Add Comment"
                                        placeholder="Add a comment..."
                                    />
                                ) : (
                                    <Button
                                        variant="outline"
                                        className="w-full"
                                        onClick={() => setShowReplyForm(true)}
                                    >
                                        Add Comment
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
};

const AIConfirmDialog: React.FC<{
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ onConfirm, onCancel }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-base font-semibold text-gray-900">Generate AI Insights?</h2>
            <p className="mb-1 text-sm text-gray-600">
                Your chart data will be sent to Google Gemini for analysis.
            </p>
            <p className="mb-5 text-sm font-medium text-amber-700">
                Do not proceed if this data contains sensitive, confidential, or personally
                identifiable information that you would not want shared with an AI model.
            </p>
            <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={onCancel}>
                    Cancel
                </Button>
                <Button onClick={onConfirm}>Generate</Button>
            </div>
        </div>
    </div>
);

const AISummaryPanel: React.FC<{
    status: "PENDING" | "DONE" | "ERROR" | null;
    summary: ChartAISummary | null;
    generatedBy: { type: string; name: string; avatarUrl: string | null } | null;
    generatedAt: string | null;
    onGenerate: () => void;
    isGenerating: boolean;
    onClear: () => void;
    isClearing: boolean;
}> = ({
    status,
    summary,
    generatedBy,
    generatedAt,
    onGenerate,
    isGenerating,
    onClear,
    isClearing,
}) => {
    if (status === "PENDING") {
        return (
            <div className="flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-700">
                <Spinner />
                <span>Generating AI insights...</span>
            </div>
        );
    }

    if (status === "ERROR") {
        return (
            <div className="rounded-lg border border-amber-100 bg-amber-50 p-4">
                <div className="flex items-start justify-between gap-4">
                    <p className="text-sm text-amber-800">AI summary could not be generated.</p>
                    <div className="flex shrink-0 gap-3">
                        <button
                            onClick={onGenerate}
                            disabled={isGenerating}
                            className="text-sm font-medium text-indigo-600 hover:underline disabled:opacity-50"
                        >
                            Try again
                        </button>
                        <button
                            onClick={onClear}
                            disabled={isClearing}
                            className="text-sm font-medium text-gray-500 hover:underline disabled:opacity-50"
                        >
                            Turn off
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (status === "DONE" && summary) {
        return (
            <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-5">
                <div className="mb-3 flex items-start justify-between gap-4">
                    <h3 className="text-sm font-semibold text-indigo-900">{summary.title}</h3>
                    <button
                        onClick={onClear}
                        disabled={isClearing}
                        className="shrink-0 text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        title="Turn off AI insights"
                    >
                        Turn off
                    </button>
                </div>
                <p className="mb-3 text-sm text-gray-700">{summary.summary}</p>
                {summary.trends.length > 0 && (
                    <ul className="mb-3 list-inside list-disc space-y-1 text-sm text-gray-600">
                        {summary.trends.map((trend, i) => (
                            <li key={i}>{trend}</li>
                        ))}
                    </ul>
                )}
                <p className="mb-3 text-sm font-medium text-indigo-800">{summary.recommendation}</p>
                {generatedBy && generatedAt && (
                    <p className="text-xs text-gray-400">
                        Generated by {generatedBy.name} on{" "}
                        {new Date(generatedAt).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                        })}
                    </p>
                )}
            </div>
        );
    }

    // No summary yet -- show opt-in button
    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-sm font-medium text-gray-700">AI Data Insights</p>
                    <p className="mt-1 text-xs text-gray-500">
                        Generate an AI-powered summary of trends and recommendations.{" "}
                        <span className="font-medium text-amber-700">
                            Do not use for sensitive or confidential data.
                        </span>
                    </p>
                </div>
                <Button variant="outline" onClick={onGenerate} disabled={isGenerating}>
                    {isGenerating ? "Requesting..." : "Generate Summary"}
                </Button>
            </div>
        </div>
    );
};
